// Deploy.js
import React, { useState, useEffect, useCallback, useContext } from "react";
import "../dashboard/Dashboard.css" 
import { fetchModelList } from "../api/modelAPI.js";
import sendDeployRequest from "../api/makeDeployment";
import deleteService from "../api/deleteDeployment";
import Deployments from "./DeploymentsTables";
import UserManagement from "./DeployUserManagement.js" 
import { setCookie, getCookie } from '../cookieUtils.js';
import { AuthContext }  from '../App.js'
import { log } from "../utils/logger.js";
import { fetchDeployments } from "../api/fetchDeployments.js";
import { getFlowNames } from "../api/fetchFlows.js";

function Deploy({ isDarkMode, userEmail, styles, depsBrah, wait, userPlan, vaults = ['-'], refreshVaults = null }) {
    const { vectorVault } = useContext(AuthContext);
    const [title, setTitle] = useState('');
    const [titleError, setTitleError] = useState('');
    const [description, setDescription] = useState('');
    const [vault, setVault] = useState('');
    const [forwardSlash, setForwardSlash] = useState('');
    const [deploymentType, setDeploymentType] = useState('chat');
    const [convFlow, setConvFlow] = useState('');
    const [edit, setEdit] = useState(false);
    const [del, setDel] = useState(false);
    const [model, setModel] = useState('default');
    const [dark, setDark] = useState(false);
    const [context, setContext] = useState(true);
    const [JSN, setJSN] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [models, setModels] = useState([]);
    const [flows, setFlows] = useState([]);
    const [showCustomModelInput, setShowCustomModelInput] = useState(false);
    const [customModelName, setCustomModelName] = useState('');
    const [deployments, setDeployments] = useState(depsBrah);
    
    // User management state
    const [isPublic, setIsPublic] = useState(true);
    const [users, setUsers] = useState([{ username: '', password: '' }]);
    
    // Edit mode state
    const [isEditMode, setIsEditMode] = useState(false);
    const [originalTitle, setOriginalTitle] = useState('');

    // Helper functions to get deployment type booleans
    const getDeploymentBooleans = () => {
        return {
            chat: deploymentType === 'chat' || deploymentType === 'chatdata',
            data: deploymentType === 'data' || deploymentType === 'chatdata',
            conv: deploymentType === 'conversation'
        };
    };
    
    log(depsBrah);
    
    // Add this useEffect to load deployments on component mount
    useEffect(() => {
        const loadDeployments = async () => {
            try {
                setIsLoading(true);
                const fetchedDeployments = await fetchDeployments(userEmail);
                
                // Filter out widget deployments
                const filteredDeployments = {};
                for (const [key, value] of Object.entries(fetchedDeployments || {})) {
                    if (!key.startsWith('widget:') && value.type !== 'widget') {
                        filteredDeployments[key] = value;
                    }
                }
                
                setDeployments(filteredDeployments);
            } catch (error) {
                console.error("Error loading deployments:", error);
                
                // If API fetch fails, use props but filter out widgets
                if (depsBrah) {
                    const filteredProps = {};
                    for (const [key, value] of Object.entries(depsBrah)) {
                        if (!key.startsWith('widget:') && value.type !== 'widget') {
                            filteredProps[key] = value;
                        }
                    }
                    setDeployments(filteredProps);
                }
            } finally {
                setIsLoading(false);
            }
        };
        
        loadDeployments();
        // Only run this on initial mount and when userEmail changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userEmail]);

    // Refresh vaults every time the deploy page is visited
    useEffect(() => {
        if (refreshVaults && !wait && userEmail) {
            refreshVaults();
        }
    }, [refreshVaults, wait, userEmail]);
    
    const refreshDeployments = useCallback(async (newDeployment = null) => {
        try {
            setIsLoading(true);
            // Fetch all deployments from the server
            const fetchedDeployments = await fetchDeployments(userEmail);
            
            // Filter out widget deployments
            const filteredDeployments = {};
            for (const [key, value] of Object.entries(fetchedDeployments || {})) {
                if (!key.startsWith('widget:') && value.type !== 'widget') {
                    filteredDeployments[key] = value;
                }
            }
            
            setDeployments(filteredDeployments);
        
            // If there's a new deployment, we've already fetched everything,
            // so no need to manually add it to the state
        } catch (error) {
            console.error("Error refreshing deployments:", error);
            
            // If fetch fails but we have a new deployment to add, add it to the current state
            if (newDeployment) {
                const title = newDeployment.title || newDeployment.TITLE;
                if (!title.startsWith("widget:") && newDeployment.type !== "widget") {
                    setDeployments(prev => ({
                        ...prev,
                        [title]: newDeployment
                    }));
                }
            }
        } finally {
            setIsLoading(false);
        }
    }, [userEmail]);
    
    // Load OpenAI models
    useEffect(() => {
        async function loadModels() {
            const fetchedModels = await fetchModelList(userEmail);
            setModels(fetchedModels);
        }
        loadModels();
    }, [userEmail]);

    // Load flows
    useEffect(() => {
        async function loadFlows() {
            try {
                const fetchedFlows = await getFlowNames(userEmail);
                // Remove the "Flow: " prefix for display
                const cleanFlows = fetchedFlows.map(flow => flow.replace('Flow: ', ''));
                setFlows(cleanFlows);
            } catch (error) {
                console.error("Error loading flows:", error);
                setFlows([]);
            }
        }
        loadFlows();
    }, [userEmail]);
    
    const handleModelChange = (e) => {
        const selectedModel = e.target.value;
        setModel(selectedModel);
        if (selectedModel === 'finetuned') {
            setShowCustomModelInput(true);
        } else {
            setShowCustomModelInput(false);
            setCustomModelName('');
        }
    };
    
    const getSelectedModel = () => {
        if (model === 'finetuned') {
            return customModelName.trim();
        } else {
            return model;
        }
    };
    
    // fetchVaults function removed - vaults are now passed as props from App.js
    
    const validateTitle = (value) => {
        // Regular expression to check for valid characters
        const validTitleRegex = /^[a-zA-Z0-9-\s]+$/;
        
        if (!validTitleRegex.test(value)) {
            setTitleError('Title can only contain letters, numbers, hyphens, and spaces.');
            return false;
        }
        setTitleError('');
        return true;
    };
    
    const handleEdit = (deployment) => {
        log(deployment)
        const deploymentTitle = deployment.title || deployment.TITLE || '';
        setTitle(deploymentTitle);
        setOriginalTitle(deploymentTitle); // Store the original title for deletion
        setIsEditMode(true); // Set edit mode
        setDescription(deployment.description || deployment.DESCRIPTION || '');
        setVault(deployment.vault || deployment.VAULT || '');
        setForwardSlash(deployment.forwardSlash || deployment.FORWARDSLASH || '');
        
        // Determine deployment type from existing deployment data
        let deployType = '';
        const hasChat = deployment.chat || deployment.CHAT;
        const hasData = deployment.data || deployment.DATA;
        const hasConv = deployment.conv || deployment.CONV;
        
        if (hasConv) {
            deployType = 'conversation';
        } else if (hasChat && hasData) {
            deployType = 'chatdata';
        } else if (hasChat) {
            deployType = 'chat';
        } else if (hasData) {
            deployType = 'data';
        }
        setDeploymentType(deployType);
        
        setConvFlow(deployment.convFlow || deployment.CONV_FLOW || '');
        setEdit(deployment.edit || deployment.EDIT || false);
        setDel(deployment.del || deployment.DELETE || false);
        setModel(deployment.model || deployment.MODEL || false);
        setDark(deployment.dark || deployment.DARK || false);
        setContext(deployment.context || deployment.CONTEXT || true);
        setJSN(deployment.JSN || deployment.JSON || false);
        
        // Fix for the isPublic field
        // Check for both lowercase and uppercase property names
        // Default to true if not specified
        const publicFlag = 
            deployment.isPublic !== undefined ? deployment.isPublic : 
            deployment.IS_PUBLIC !== undefined ? deployment.IS_PUBLIC : 
            true;
        setIsPublic(publicFlag);
        
        // Fix for the users field
        // Check for both users and AUTH_USERS arrays
        // If users exist, use them; otherwise create a default user entry
        if (
            (deployment.users && Array.isArray(deployment.users) && deployment.users.length > 0) ||
            (deployment.AUTH_USERS && Array.isArray(deployment.AUTH_USERS) && deployment.AUTH_USERS.length > 0)
        ) {
            // Use either users or AUTH_USERS, whichever exists
            const usersList = deployment.users || deployment.AUTH_USERS;
            setUsers(usersList);
        } else {
            setUsers([{ username: '', password: '' }]);
        }
    };
    
    const handleDelete = async (title) => {
        if (window.confirm(`Are you sure you want to delete the deployment "${title}"?`)) {
            try {
                setIsLoading(true);
                const response = await deleteService(userEmail, title);
                log(response);
                setIsLoading(false);
                await refreshDeployments();
                alert(`Deployment "${title}" deleted successfully.`);
                
                setDeployments(prev => {
                    const newDeployments = { ...prev };
                    delete newDeployments[title];
                    return newDeployments;
                });
            } catch (error) {
                console.error("Error deleting deployment:", error);
                setIsLoading(false);
                alert(`Failed to delete deployment "${title}". Please try again.`);
            }
        }
    };
    
    const handleTitleChange = (e) => {
        const newTitle = e.target.value;
        setTitle(newTitle);
        validateTitle(newTitle);
    };
    
    // User management handlers
    const handleAddUser = () => {
        setUsers([...users, { username: '', password: '' }]);
    };
    
    const handleRemoveUser = (index) => {
        const newUsers = [...users];
        newUsers.splice(index, 1);
        setUsers(newUsers);
    };
    
    const handleUserChange = (index, field, value) => {
        const newUsers = [...users];
        newUsers[index][field] = value;
        setUsers(newUsers);
    };

    // Handle deployment type change
    const handleDeploymentTypeChange = (e) => {
        const newType = e.target.value;
        setDeploymentType(newType);
        
        // Clear conversation flow if not conversation type
        if (newType !== 'conversation') {
            setConvFlow('');
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateTitle(title)) {
            return; // Stop submission if title is invalid
        }
        
        // Validate users data if not public
        if (!isPublic) {
            const validUsers = users.filter(user => user.username.trim() && user.password.trim());
            if (validUsers.length === 0) {
                alert("You must add at least one user with username and password when access is not public.");
                return;
            }
            // Only keep valid users (with both username and password)
            setUsers(validUsers);
        }
        
        if (userPlan !== 'free') {
            // Check if at least one deployment type is selected
            if (deploymentType) {
                // Validate conversation flow is selected if conversation is enabled
                if (deploymentType === 'conversation' && !convFlow) {
                    alert("You must select a flow when conversation deployment is enabled.");
                    return;
                }
                

                
                if (title && title.trim() && (deploymentType === 'conversation' || (vault && vault.trim())) && forwardSlash && forwardSlash.trim()) {
                    try {
                        setIsLoading(true)
                        
                        // If we're editing an existing deployment, delete the old one first
                        if (isEditMode && originalTitle) {
                            try {
                                await deleteService(userEmail, originalTitle);
                                log(`Deleted old deployment: ${originalTitle}`);
                            } catch (error) {
                                console.error("Error deleting old deployment:", error);
                                // Continue with deployment even if deletion fails
                            }
                        }
                        
                        // Prepare users data
                        const usersData = isPublic ? [] : users.filter(user => user.username && user.password);
                        
                        const { chat, data, conv } = getDeploymentBooleans();
                        
                        const response = await sendDeployRequest(
                            userEmail, title, 
                            deploymentType === 'conversation' ? '' : vault, 
                            forwardSlash, 
                            chat, data, edit, del, 
                            deploymentType === 'conversation' ? '' : getSelectedModel(),
                            dark, context, description, JSN,
                            isPublic, usersData, // Add new parameters for access control
                            conv, convFlow, // Add conversation parameters
                            vectorVault
                        );
                        log(response);
                        
                        // Create new deployment object
                        const newDeployment = {
                            TITLE: title,
                            DOMAIN: response.domain,
                            FORWARDSLASH: forwardSlash,
                            VAULT: deploymentType === 'conversation' ? '' : vault,
                            DESCRIPTION: description,
                            CHAT: chat,
                            DATA: data,
                            CONV: conv,
                            CONV_FLOW: convFlow,
                            EDIT: edit,
                            DELETE: del,
                            MODEL: deploymentType === 'conversation' ? '' : getSelectedModel(),
                            DARK: dark,
                            CONTEXT: context,
                            JSON: JSN,
                            isPublic: isPublic,
                            users: isPublic ? [] : usersData
                        };
                        
                        // Refresh the deployments list with the new deployment
                        refreshDeployments(newDeployment);
                        setIsLoading(false)
                        alert(`Deployed successfully at url: ${response.domain} You can manage your active deployments below. \n*First deployment takes 15-30 mintues for the domain to go live.`)
                        
                        // Reset form
                        setTitle('');
                        setDescription('');
                        setVault('');
                        setForwardSlash('');
                        setDeploymentType('chat');
                        setConvFlow('');
                        setEdit(false);
                        setDel(false);
                        setModel('default');
                        setDark(false);
                        setContext(true);
                        setJSN(false);
                        setIsPublic(true);
                        setUsers([{ username: '', password: '' }]);
                        setIsEditMode(false);
                        setOriginalTitle('');
                        setIsLoading(false);
                    } catch (error) {
                        console.error("Error in POST request:", error);
                        setIsLoading(false);
                        alert("Error creating deployment. Please try again.");
                    }
                } else { 
                    setIsLoading(false)
                    const requiredFields = deploymentType === 'conversation' 
                        ? "'Title' and 'Path'"
                        : "'Title', 'Vault', and 'Path'";
                    alert(`${requiredFields} are all required variables that cannot be left blank`)
                };
            } else { // no deployment type selected
                setIsLoading(false)
                alert("You must select a deployment type. Choose from 'Chat', 'Data', 'Chat & Data', or 'Conversation'.")
            };
        } else { // on the free plan
            setIsLoading(false)
            alert('You must be a paying user to make a deployment. Click "SignUp" in the sidebar to conitnue.')
        };
    };
    
    return (
        <div style={{...styles.wrapper }}>
            <div className="App" style={{...styles.body, paddingTop: "50px"}}>
                <h1 style={{ marginBottom: '10px' }}>Deployments</h1>
                <p style={{ marginBottom: '30px' }}>
                Deployments are $10/mo and they make a live webstie for your vault. It gives you a link so you can share. 
                These Deployments are quick and easy ways to share the AI Agents and Chatbots you build. 
                Choose how visitors interact with your AI, and whether they should just chat with that AI, be able to see the data too, or view conversation analytics. Full control over read, write and delete. 
                If you have any questions, reach out to our support.
                </p>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {/* Deployment Type Selection */}
                    <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                        Deployment Type:
                        <select
                            value={deploymentType}
                            onChange={handleDeploymentTypeChange}
                            className={isDarkMode ? 'sleek-select-dark' : 'sleek-select-light'}
                            style={{ marginTop: '5px' }}
                        >
                            <option value="chat">Chat</option>
                            <option value="data">Data</option>
                            <option value="chatdata">Chat & Data</option>
                            <option value="conversation">Conversation</option>
                        </select>
                        <small style={{ 
                            marginTop: '5px', 
                            color: isDarkMode ? '#999999' : '#6b7280',
                            fontSize: '0.85em'
                        }}>
                            {deploymentType === 'chat' && 'Allow visitors to chat with your AI'}
                            {deploymentType === 'data' && 'Allow visitors to view and interact with your data'}
                            {deploymentType === 'chatdata' && 'Allow visitors to chat and view data'}
                            {deploymentType === 'conversation' && 'Show conversation analytics interface'}
                        </small>
                    </label>
                    
                    {/* Title input */}
                    <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                        Title:
                        <input
                            type="text"
                            value={title}
                            onChange={handleTitleChange}
                            className={isDarkMode ? 'sleek-input-api-dark' : 'sleek-input-api-light'}
                            style={{ marginTop: '5px' }}
                        />
                        {titleError && <span style={{ color: 'red', fontSize: '0.8em' }}>{titleError}</span>}
                    </label>
                    
                    {/* Description input */}
                    <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                        Description:
                        <input
                            type="text"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            className={isDarkMode ? 'sleek-input-api-dark' : 'sleek-input-api-light'}
                            style={{ marginTop: '5px' }}
                        />
                    </label>
                    
                    {/* Vault input */}
                    {deploymentType !== 'conversation' && (
                        <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                            Vault:
                            <select
                                value={vault}
                                onChange={e => setVault(e.target.value)}
                                className={isDarkMode ? 'sleek-select-dark' : 'sleek-select-light'}
                                style={{ marginTop: '5px' }}
                            >
                                <option value="">Select a vault</option>
                                {vaults.map((v, index) => (
                                    <option key={index} value={v}>{v}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    
                    {/* Path input */}
                    <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                        Path:
                        <input
                            type="text"
                            value={forwardSlash}
                            onChange={e => setForwardSlash(e.target.value)}
                            className={isDarkMode ? 'sleek-input-api-dark' : 'sleek-input-api-light'}
                            style={{ marginTop: '5px' }}
                        />
                    </label>
                    
                    {/* Model Selection */}
                    {deploymentType !== 'conversation' && (
                        <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                            Model:
                            <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                                <select
                                    className={isDarkMode ? 'sleek-select-dark' : 'sleek-select-light'}
                                    value={model}
                                    onChange={handleModelChange}
                                    style={{ flex: 1 }}
                                >
                                    <option value="">Select a model</option>
                                    {model && model !== 'finetuned' && !models.some(item => item.model === model) && (
                                        <option value={model}>Legacy — {model}</option>
                                    )}
                                    {models.map((modelItem) => (
                                        <option key={modelItem.model} value={modelItem.model}>
                                            {modelItem.model}
                                        </option>
                                    ))}
                                    <option value="finetuned">Finetuned Model</option>
                                </select>
                                {showCustomModelInput && (
                                    <input
                                        type="text"
                                        placeholder="Enter fine-tuned model name"
                                        value={customModelName}
                                        onChange={(e) => setCustomModelName(e.target.value)}
                                        className={isDarkMode ? 'sleek-input-api-dark' : 'sleek-input-api-light'}
                                        style={{ flex: 1 }}
                                    />
                                )}
                            </div>
                        </label>
                    )}

                    {/* Flow Selection - only show when conversation is selected */}
                    {deploymentType === 'conversation' && (
                        <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                            Target Flow:
                            <select
                                value={convFlow}
                                onChange={e => setConvFlow(e.target.value)}
                                className={isDarkMode ? 'sleek-select-dark' : 'sleek-select-light'}
                                style={{ marginTop: '5px' }}
                            >
                                <option value="">Select a flow</option>
                                {flows.map((flow, index) => (
                                    <option key={index} value={flow}>{flow}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    
                    {/* Other Options */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px' }}>
                        {[
                            { key: 'isPublic', displayName: 'Public', tooltip: 'Make this deployment accessible to the public without login' },
                            ...(deploymentType !== 'conversation' ? [
                                { key: 'edit', displayName: 'Edit', tooltip: 'Allow the adding and editing of data' },
                                { key: 'del', displayName: 'Delete', tooltip: 'Allow the deleting of data' },
                                { key: 'context', displayName: 'Context', tooltip: 'By default, all responses are RAG responses using your Vault. Uncheck if you want to turn off RAG responses and NOT use the data in your Vault as context.' },
                                { key: 'JSN', displayName: 'JSON', tooltip: 'Allow users to download/upload the entire database to/from JSON' }
                            ] : []),
                            { key: 'dark', displayName: 'Dark Mode', tooltip: 'Use Dark mode (Defaults to light mode)' }
                        ].map(({ key, displayName, tooltip }) => (
                            <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: '10px' }}>
                                <label style={{ display: 'flex', alignItems: 'center' }} title={tooltip}>
                                    {displayName}:
                                    <input
                                        type="checkbox"
                                        checked={eval(key)}
                                        onChange={e => {
                                            log(`Setting ${key}: ${e.target.checked}`); 
                                            if (key === 'isPublic') {
                                                setIsPublic(e.target.checked);
                                            } else {
                                                eval(`set${key.charAt(0).toUpperCase() + key.slice(1)}(e.target.checked)`);
                                            }
                                        }}
                                        className={isDarkMode ? 'sleek-checkbox-dark' : 'sleek-checkbox-light'}
                                        style={{ marginLeft: '5px' }}
                                        title={tooltip}
                                    />
                                </label>
                            </div>
                        ))}
                    </div>
                    
                    {/* User Management Component - now only handles the user table, not the Public checkbox */}
                    <UserManagement
                      isDarkMode={isDarkMode}
                      isPublic={isPublic}
                      setIsPublic={setIsPublic}
                      users={users}
                      handleUserChange={handleUserChange}
                      handleAddUser={handleAddUser}
                      handleRemoveUser={handleRemoveUser}
                    />
                    
                    <div style={{ display: 'flex', gap: '10px', alignSelf: 'start' }}>
                        <button className='button' type="submit">
                            {isEditMode ? 'Update Deployment' : 'Deploy'}
                        </button>
                        {isEditMode && (
                            <button 
                                type="button" 
                                className='button' 
                                style={{ backgroundColor: '#222222' }}
                                onClick={() => {
                                    // Reset form to initial state
                                    setTitle('');
                                    setDescription('');
                                    setVault('');
                                    setForwardSlash('');
                                    setDeploymentType('chat');
                                    setConvFlow('');
                                    setEdit(false);
                                    setDel(false);
                                    setModel('default');
                                    setDark(false);
                                    setContext(true);
                                    setJSN(false);
                                    setIsPublic(true);
                                    setUsers([{ username: '', password: '' }]);
                                    setIsEditMode(false);
                                    setOriginalTitle('');
                                }}
                            >
                                Cancel Edit
                            </button>
                        )}
                    </div>
                    <div className="loading-spinner" style={{ display: isLoading ? 'block' : 'none' }}>
                        <svg viewBox="0 0 50 50">
                            <circle cx="25" cy="25" r="20" stroke="#00d67a" strokeWidth="5" fill="none" />
                        </svg>
                    </div>
                </form>
                <Deployments isDarkMode={isDarkMode} styles={styles} depsBrah={deployments} wait={wait} onEdit={handleEdit} onDelete={handleDelete} userEmail={userEmail} />
            </div>
        </div>
    );    
}

export default Deploy;